import {
  Suspense,
  lazy,
  useEffect,
  useState,
  type ComponentType,
  type LazyExoticComponent,
} from "react";
import { useParams } from "react-router";
import Loader from "@/components/UI/Loader";
import MarkdownRenderer from "@/components/UI/MarkdownRenderer";
import "@/components/Wiki/wiki-blocks.css";
import { WikiPageShell } from "@/views/Wiki/WikiPageShell";
import { loadMarkdown } from "@/utils/markdownLoader";
import AllReleaseNotesPage from "./release-notes/AllReleaseNotes";
import ReleaseNotePage from "./release-notes/ReleaseNotePage";
import { scrollToWikiTop } from "./utils/scrollToWikiTop";

// ── Guide page registry ───────────────────────────────────────────────────────
// All pages are lazily imported — each becomes a separate code chunk loaded only
// when that specific route is visited. Add new guides here; the render logic
// below never needs to change.

const COMPONENT_PAGES: Record<string, LazyExoticComponent<ComponentType>> = {
  // Inquiry (both canonical slugs and legacy aliases used by nav/hub)
  "inquiry-alerts": lazy(() => import("./guides/InquiryAlerts")),
  "alerts-enhanced": lazy(() => import("./guides/InquiryAlerts")),
  "inquiry-insights": lazy(() => import("./guides/InquiryInsights")),
  "insights-enhanced": lazy(() => import("./guides/InquiryInsights")),
  "inquiry-askvivo": lazy(() => import("./guides/InquiryAskVivo")),
  "inquiry-askvivo-user-guidelines": lazy(
    () => import("./guides/InquiryAskVivoUserGuidelines"),
  ),
  // Basics
  "basics-what-is-vivo": lazy(() => import("./guides/BasicsWhatIsVivo")),
  "basics-navigating-vivo": lazy(() => import("./guides/BasicsNavigatingVivo")),
  "basics-screen-types": lazy(() => import("./guides/BasicsScreenTypes")),
  // Exploration
  "exploration-workspaces": lazy(
    () => import("./guides/ExplorationWorkspaces"),
  ),
  "exploration-dashboard-studio-overview": lazy(
    () => import("./guides/ExplorationDashboardStudioOverview"),
  ),
  "exploration-table-tab": lazy(() => import("./guides/ExplorationTableTab")),
  "exploration-dashboard-tab": lazy(
    () => import("./guides/ExplorationDashboardTab"),
  ),
  "exploration-plot-builder": lazy(
    () => import("./guides/ExplorationPlotBuilder"),
  ),
  // Data
  "data-connections": lazy(() => import("./guides/DataConnections")),
  "data-discrepancies": lazy(() => import("./guides/DataDiscrepancies")),
  // Domain — Sites
  "exploration-sites-overview": lazy(
    () => import("./guides/DomainsSitesOverview"),
  ),
  "exploration-sites-protocol-deviations": lazy(
    () => import("./guides/DomainsSitesProtocolDeviations"),
  ),
  "exploration-reg-docs-tracker": lazy(
    () => import("./guides/DomainsSitesRegDocsTracker"),
  ),
  "exploration-site-feasibility-board": lazy(
    () => import("./guides/DomainsSitesFeasibilityBoard"),
  ),
  // Domain — Enrollment
  "exploration-trial-enrollment": lazy(
    () => import("./guides/DomainsTrialEnrollment"),
  ),
  // Domain — Subjects
  "subjects-overview": lazy(() => import("./guides/DomainsSubjectsOverview")),
  "exploration-subjects-visits-activities": lazy(
    () => import("./guides/DomainsSubjectsVisitsActivities"),
  ),
  // Domain — Safety
  "exploration-safety-adverse-events": lazy(
    () => import("./guides/DomainsSafetyAdverseEvents"),
  ),
  "exploration-safety-ip-admin": lazy(
    () => import("./guides/DomainsSafetyIpAdmin"),
  ),
  "exploration-safety-ip-dispensation": lazy(
    () => import("./guides/DomainsSafetyIpDispensation"),
  ),
  "exploration-safety-pharmacokinetics": lazy(
    () => import("./guides/DomainsSafetyPharmacokinetics"),
  ),
  "exploration-safety-concomitant-medications": lazy(
    () => import("./guides/DomainsSafetyConmeds"),
  ),
  "exploration-safety-concomitant-procedures": lazy(
    () => import("./guides/DomainsSafetyConcomitantProcedures"),
  ),
  // Domain — Measures
  "exploration-measures-assessments": lazy(
    () => import("./guides/DomainsMeasuresAssessments"),
  ),
  "exploration-measures-imaging": lazy(
    () => import("./guides/DomainsMeasuresImaging"),
  ),
  "exploration-measures-labs": lazy(
    () => import("./guides/DomainsMeasuresLabs"),
  ),
  "exploration-measures-questionnaires": lazy(
    () => import("./guides/DomainsMeasuresQuestionnaires"),
  ),
  // Single Subject Profiles
  "exploration-singlesubject-schedule": lazy(
    () => import("./guides/DomainsSingleSubjectSchedule"),
  ),
  "exploration-singlesubject-screening": lazy(
    () => import("./guides/DomainsSingleSubjectScreening"),
  ),
  "exploration-singlesubject-timeline": lazy(
    () => import("./guides/DomainsSingleSubjectTimeline"),
  ),
  "exploration-singlesubject-subject-ae": lazy(
    () => import("./guides/DomainsSingleSubjectSubjectAE"),
  ),
  "exploration-singlesubject-ip-administration": lazy(
    () => import("./guides/DomainsSingleSubjectIpAdministration"),
  ),
  "exploration-singlesubject-ip-dispensation": lazy(
    () => import("./guides/DomainsSingleSubjectIpDispensation"),
  ),
  "exploration-singlesubject-missed-doses": lazy(
    () => import("./guides/DomainsSingleSubjectMissedDoses"),
  ),
  "exploration-singlesubject-pharmacokinetics": lazy(
    () => import("./guides/DomainsSingleSubjectPharmacokinetics"),
  ),
  "exploration-singlesubject-conmeds": lazy(
    () => import("./guides/DomainsSingleSubjectConmeds"),
  ),
  "exploration-singlesubject-conprocedures": lazy(
    () => import("./guides/DomainsSingleSubjectConcomitantProcedures"),
  ),
  "exploration-singlesubject-assessments": lazy(
    () => import("./guides/DomainsSingleSubjectAssessments"),
  ),
  "exploration-singlesubject-imaging": lazy(
    () => import("./guides/DomainsSingleSubjectImaging"),
  ),
  "exploration-singlesubject-labs": lazy(
    () => import("./guides/DomainsSingleSubjectLabs"),
  ),
  "exploration-singlesubject-questionnaires": lazy(
    () => import("./guides/DomainsSingleSubjectQuestionnaires"),
  ),
  "exploration-singlesubject-data-discrepancies": lazy(
    () => import("./guides/DomainsSingleSubjectDataDiscrepancies"),
  ),
  "exploration-singlesubject-deviations": lazy(
    () => import("./guides/DomainsSingleSubjectDeviations"),
  ),
  // Admin
  "admin-portal": lazy(() => import("./guides/AdminPortal")),
  "admin-users-access": lazy(() => import("./guides/AdminPortal")),
  "admin-roles-permissions": lazy(() => import("./guides/AdminPortal")),
  "admin-activity-log": lazy(() => import("./guides/AdminPortal")),
};

// Shared loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center">
    <Loader type="blue" />
  </div>
);

type MarkdownGuide = {
  title: string;
  description: string;
  lastUpdated: string;
  content: string;
};

type MarkdownGuideState = {
  slug: string;
  guide: MarkdownGuide | null;
};

// ── WikiPage ──────────────────────────────────────────────────────────────────

const WikiPage = () => {
  const { type, slug } = useParams();
  const [markdownGuide, setMarkdownGuide] = useState<MarkdownGuideState | null>(
    null,
  );

  // Resolve the component for this route (if any). Checked before render
  // so component pages skip the markdown loader entirely.
  const ComponentPage =
    type === "release-notes" && slug === "all-release-notes"
      ? AllReleaseNotesPage
      : ((slug ? COMPONENT_PAGES[slug] : null) ?? null);

  useEffect(() => {
    // Scroll to top on navigation
    scrollToWikiTop();
  }, [type, slug]);

  useEffect(() => {
    if (type !== "guides" || !slug || COMPONENT_PAGES[slug]) {
      return;
    }

    let mounted = true;

    loadMarkdown(`guides/${slug}`).then((data) => {
      if (!mounted) return;

      setMarkdownGuide({
        slug,
        guide: data
          ? {
              title: data.frontmatter.title || slug,
              description: data.frontmatter.description || "",
              lastUpdated: data.frontmatter.lastUpdated || "",
              content: data.content,
            }
          : null,
      });
    });

    return () => {
      mounted = false;
    };
  }, [type, slug]);

  // Component pages render immediately with Suspense handling the lazy-load delay.
  // Checked before the loading gate so TSX guides never show an unnecessary spinner.
  if (type === "release-notes" && slug && slug !== "all-release-notes") {
    return <ReleaseNotePage slug={slug} />;
  }

  if (ComponentPage) {
    return (
      <Suspense fallback={<PageLoader />}>
        <ComponentPage />
      </Suspense>
    );
  }

  if (type === "guides" && slug) {
    if (!markdownGuide || markdownGuide.slug !== slug) return <PageLoader />;
    if (!markdownGuide.guide)
      return <div className="flex m-6">Page not found</div>;

    return (
      <WikiPageShell
        header={{
          eyebrow: "VIVO · GUIDE",
          title: markdownGuide.guide.title,
          lede: markdownGuide.guide.description,
          updated: markdownGuide.guide.lastUpdated,
        }}
      >
        <MarkdownRenderer content={markdownGuide.guide.content} />
      </WikiPageShell>
    );
  }

  return <div className="flex m-6">Page not found</div>;
};

export default WikiPage;
